import { UpperCasePipe } from '@angular/common';
import {
  Component,
  Inject,
  inject,
  input,
  OnInit,
  output,
  ChangeDetectionStrategy,
} from '@angular/core';
import { IQBOOST_V2_FEATURE_FLAG } from '@maximizer/ai/data-access';
import { FeatureService } from '@maximizer/core/shared/data-access';
import { CDN_URL } from '@maximizer/core/shared/domain';
import { TrackerService } from '@maximizer/outlook/shared/data-access';
import { TranslatePipe } from '@ngx-translate/core';
import { KENDO_BUTTON } from '@progress/kendo-angular-buttons';
import { KENDO_DIALOG } from '@progress/kendo-angular-dialog';
import {
  EventAction,
  EventJourney,
  EventModule,
} from '@maximizer/outlook/shared/domain';

@Component({
  selector: 'maximizer-outlook-iq-boost-privacy-security',
  template: `
    <kendo-dialog width="95%" maxWidth="500px">
      <div
        class="reset flex items-center justify-between bg-neutral-10 px-2 py-1"
      >
        <p class="subtitle-1 text-black">
          {{ 'outlook.iqBoost.privacySecurity.title' | translate }}
        </p>
        <button
          kendoButton
          themeColor="tertiary"
          iconClass="fa-icons icon-xmark icon-14"
          [attr.aria-label]="'outlook.buttons.close' | translate"
          class="small"
          (click)="closeDialog.emit()"
        ></button>
      </div>

      <div class="flex flex-col gap-4 p-4">
        <p class="text-body large text-bush-green-160">
          {{ 'outlook.iqBoost.privacySecurity.description' | translate }}
        </p>

        <div class="flex flex-col gap-3">
          <div class="flex items-center gap-2">
            @if (anonymizerEnabled()) {
              <span
                class="maximizer-icons icon-anonymizer icon-16 shrink-0 text-bush-green-100"
              ></span>
            } @else {
              <span
                class="fa-icons icon-unlock icon-16 shrink-0 text-neutral-60"
              ></span>
            }
            <p class="text-body large text-bush-green-160">
              <strong>{{
                'outlook.iqBoost.privacySecurity.anonymizerLabel' | translate
              }}</strong>
              {{ 'outlook.iqBoost.privacySecurity.anonymizerIs' | translate }}
              <strong>{{ anonymizerStatus() }}</strong>
            </p>
          </div>

          <div class="flex items-center gap-2">
            @if (dataSourceEnabled()) {
              <img
                class="w-4 shrink-0 bg-black rounded self-center"
                [src]="cdn + '/assets/images/logo.svg'"
                alt="maximizer"
              />
            } @else {
              <span
                class="fa-icons icon-globe icon-16 shrink-0 text-neutral-60"
              ></span>
            }
            <p class="text-body large text-bush-green-160">
              <strong>{{
                'outlook.iqBoost.privacySecurity.dataSourceLabel' | translate
              }}</strong>
              {{
                'outlook.iqBoost.privacySecurity.dataSourceIsSetTo' | translate
              }}
              <strong>{{ dataSource() }}</strong>
            </p>
          </div>

          <div class="flex items-center gap-2">
            @if (serverRegionEnabled()) {
              <span
                class="svg-icons icon-flag-canada icon-16 shrink-0 pt-0.5"
              ></span>
            } @else {
              <span
                class="fa-icons icon-earth-americas icon-16 shrink-0 text-neutral-60"
              ></span>
            }
            <p class="text-body large text-bush-green-160">
              <strong>{{
                'outlook.iqBoost.privacySecurity.serversLabel' | translate
              }}</strong>
              {{
                'outlook.iqBoost.privacySecurity.serversAreSetTo' | translate
              }}
              <strong>{{ serverRegion() }}</strong>
            </p>
          </div>
        </div>

        <p class="text-body large text-bush-green-160">
          {{ noticeKey | translate }}
        </p>

        <div class="flex justify-center py-3">
          <button kendoButton themeColor="primary" (click)="closeDialog.emit()">
            {{ 'outlook.buttons.ok' | translate | uppercase }}
          </button>
        </div>
      </div>
    </kendo-dialog>
  `,
  styles: ':host { display: flex; flex-direction: column; }',
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [TranslatePipe, KENDO_BUTTON, KENDO_DIALOG, UpperCasePipe],
})
export class OutlookIqBoostPrivacySecurityComponent implements OnInit {
  anonymizerStatus = input<string>('');
  dataSource = input<string>('');
  serverRegion = input<string>('');
  anonymizerEnabled = input<boolean>(true);
  dataSourceEnabled = input<boolean>(true);
  serverRegionEnabled = input<boolean>(true);

  closeDialog = output<void>();

  tracker = inject(TrackerService);
  private readonly feature = inject(FeatureService);

  protected readonly noticeKey = this.feature.isFeatureOn(
    IQBOOST_V2_FEATURE_FLAG,
    false,
  )
    ? 'outlook.iqBoost.privacySecurity.adminNotice'
    : 'outlook.iqBoost.privacySecurity.adminNoticeClassic';

  constructor(@Inject(CDN_URL) readonly cdn: string) {}

  ngOnInit(): void {
    this.tracker.trackEvent({
      module: EventModule.IqBoost,
      journey: EventJourney.SecurityCenter,
      action: EventAction.Opened,
    });
  }
}
